import { getBrowserApiBase } from "./browser-api-base";

export interface RiskFlowReactionPoint {
  time: number;
  close: number;
  high?: number;
  low?: number;
}

export interface RiskFlowReactionQuote {
  label: string;
  price: number | null;
  change: number | null;
  changePercent: number | null;
  sparkline: RiskFlowReactionPoint[];
  sparkline1d: RiskFlowReactionPoint[];
  sparkline5d: RiskFlowReactionPoint[];
  historySource: "public" | "yahoo" | "unavailable";
}

const API_BASE = getBrowserApiBase();
const QUOTE_CACHE_TTL_MS = 5 * 60 * 1000;
let quoteCache: Promise<RiskFlowReactionQuote[]> | null = null;
let quoteCacheAt = 0;

export function fetchRiskFlowEconReactionQuotes(
  options: { force?: boolean } = {},
): Promise<RiskFlowReactionQuote[]> {
  const stale = Date.now() - quoteCacheAt > QUOTE_CACHE_TTL_MS;
  if (options.force || stale || !quoteCache) {
    quoteCacheAt = Date.now();
    quoteCache = fetch(`${API_BASE}/api/market-scan/macro-watchlist`, {
      signal: AbortSignal.timeout(6000),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json: { data?: unknown } | null) =>
        Array.isArray(json?.data)
          ? json.data.map(parseQuote).filter(isReactionQuote)
          : [],
      )
      .catch(() => []);
  }
  return quoteCache;
}

function parseQuote(value: unknown): RiskFlowReactionQuote | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const label = typeof record.label === "string" ? record.label : "";
  if (!label) return null;
  return {
    label,
    price: finiteNumberOrNull(record.price),
    change: finiteNumberOrNull(record.change),
    changePercent: finiteNumberOrNull(record.changePercent),
    sparkline: parsePoints(record.sparkline),
    sparkline1d: parsePoints(record.sparkline1d),
    sparkline5d: parsePoints(record.sparkline5d),
    historySource:
      record.historySource === "public" || record.historySource === "yahoo"
        ? record.historySource
        : "unavailable",
  };
}

function isReactionQuote(
  value: RiskFlowReactionQuote | null,
): value is RiskFlowReactionQuote {
  return Boolean(value);
}

function parsePoints(value: unknown): RiskFlowReactionPoint[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const time = finiteNumberOrNull(record.time);
      const close = finiteNumberOrNull(record.close);
      if (time == null || close == null) return null;
      return {
        time,
        close,
        ...(finiteNumberOrNull(record.high) != null
          ? { high: finiteNumberOrNull(record.high) as number }
          : {}),
        ...(finiteNumberOrNull(record.low) != null
          ? { low: finiteNumberOrNull(record.low) as number }
          : {}),
      };
    })
    .filter((point): point is RiskFlowReactionPoint => Boolean(point));
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
